<template>
  <v-container fluid class="expense-list-page pa-0">
    <Create
      :createDialog="createDialog"
      @close="createDialog = false"
      @submit="getList"
    />

    <Edit
      :editDialog="editDialog"
      @close="editDialog = false"
      @submit="getList"
      :editExpense="selected"
    />

    <v-card :loading="loading" outlined class="expense-list-card">
      <div class="expense-list-header">
        <div>
          <div class="page-title">Expense</div>
          <div class="page-subtitle">
            {{ expenses.length }} expense{{ expenses.length === 1 ? "" : "s" }} found
          </div>
        </div>

        <v-btn
          v-if="checkRightStatus(4)"
          small
          rounded
          color="primary"
          dark
          @click="add"
        >
          <v-icon left small>mdi-plus</v-icon>
          New Expense
        </v-btn>
      </div>

      <div class="expense-filter-bar">
        <v-text-field
          v-model="search"
          outlined
          prepend-inner-icon="search"
          autofocus
          dense
          label="Search expense"
          single-line
          hide-details
          clearable
          class="expense-search"
        />

        <v-spacer />

        <DateTimePicker
          :defaultRange="defaultDateRange"
          :range="true"
          :hideTime="true"
          v-model="dateTime"
          @submit="getList()"
        />
      </div>

      <v-card-text class="pa-4">
        <div class="summary-row mb-4">
          <div class="summary-box">
            <div class="summary-label">List Total</div>
            <div class="summary-value">{{ total | currency }}</div>
          </div>

          <div class="summary-box">
            <div class="summary-label">Result Total</div>
            <div class="summary-value">{{ fullTotal | currency }}</div>
          </div>
        </div>

        <v-data-table
          :mobile-breakpoint="0"
          :headers="headers"
          :items="expenses"
          :sort-desc="true"
          :search="search"
          :loading="loading"
          :sort-by="sortBy"
          @current-items="updateTotal"
          :page.sync="page"
          :items-per-page="itemsPerPage"
          class="expense-table"
        >
          <template v-slot:item="{ item }">
            <tr class="pointer expense-row">
              <td class="text-left">
                {{ item.date | moment("MM/DD/YYYY") }}
              </td>

              <td class="text-left text-capitalize">
                {{ item.Payee ? item.Payee.name : "Unknown Payee" }}
              </td>

              <td class="text-left">
                {{ item.note }}
              </td>

              <td class="text-right font-weight-bold">
                {{ item.amount | currency }}
              </td>

              <td class="text-left text-capitalize">
                {{ item.User ? item.User.name : "Unknown User" }}
              </td>

              <td class="justify-end align-center text-right">
                <v-menu bottom left offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon small>
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>

                  <v-list dense class="menu-button">
                    <v-list-item
                      v-if="checkRightStatus(20)"
                      @click="editExpense(item)"
                    >
                      <v-list-item-title>Edit</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                      v-if="checkRightStatus(12)"
                      @click="deleteExpense(item)"
                    >
                      <v-list-item-title class="red--text">
                        Delete
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>

          <v-alert slot="no-results" :value="true" color="error" icon="warning">
            Your search for "{{ search }}" found no results.
          </v-alert>

          <template v-slot:body.append v-if="expenses.length > 0">
            <tr class="table-total-row">
              <td colspan="3" class="text-right font-weight-bold">
                List Total
              </td>

              <td class="text-right font-weight-bold">
                {{ total | currency }}
              </td>

              <td colspan="2"></td>
            </tr>

            <tr class="table-result-row">
              <td colspan="3" class="text-right font-weight-bold">
                Result Total
              </td>

              <td class="text-right font-weight-bold">
                {{ fullTotal | currency }}
              </td>

              <td colspan="2"></td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import expenseService from "../service";
import DateTimePicker from "../../../components/DateTimePicker";
import { mapGetters } from "vuex";

const Create = () => import("./Create");
const Edit = () => import("./Edit");

export default {
  name: "expense-list",

  components: {
    Create,
    DateTimePicker,
    Edit,
  },

  data() {
    return {
      dateTime: {
        start: this.$moment().startOf("day").toISOString(),
        end: this.$moment().endOf("day").toISOString(),
      },
      createDialog: false,
      editDialog: false,
      selected: null,
      total: 0,
      fullTotal: 0,
      headers: [
        {
          text: this.$t("labels.createdAt"),
          align: "left",
          value: "date",
          sortable: true,
        },
        {
          text: "Payee",
          align: "left",
          value: "Payee.name",
          sortable: true,
        },
        {
          text: "Note",
          value: "note",
          sortable: true,
        },
        {
          text: "Amount",
          value: "amount",
          align: "right",
          sortable: true,
        },
        {
          text: "Created By",
          value: "User.name",
          sortable: true,
        },
        {
          text: "",
          value: "action",
          sortable: false,
        },
      ],
      expenses: [],
      search: null,
      loading: true,
      page: 1,
      sortBy: "createdAt",
      pageCount: 0,
      itemsPerPage: 10,
    };
  },

  computed: {
    ...mapGetters("global", [
      "currentUser",
      "checkRightStatus",
      "currentBusiness",
    ]),

    defaultDateRange() {
      if (
        this.currentBusiness &&
        this.currentBusiness.metadata &&
        this.currentBusiness.metadata.invoiceViewLimit
      ) {
        return this.currentBusiness.metadata.invoiceViewLimit.range;
      }

      return this.$vuetify.breakpoint.mdAndUp
        ? "since15DaysAgo"
        : "since60DaysAgo";
    },
  },

  created() {
    this.getList();
    this.$events.listen("expenseListEvent", () => this.getList());
  },

  beforeDestroy() {
    this.$events.remove("expenseListEvent");
  },

  methods: {
    updateTotal(val) {
      this.total = val.reduce((sum, row) => {
        return sum + Number(row.amount || 0);
      }, 0);
    },

    add() {
      this.createDialog = true;
    },

    async getList() {
      this.loading = true;
      this.editDialog = false;
      this.createDialog = false;

      try {
        const response = await expenseService.getAll({
          start: this.dateTime.start,
          end: this.dateTime.end,
        });

        this.expenses = response.data || [];

        this.fullTotal = this.expenses.reduce((sum, row) => {
          return sum + Number(row.amount || 0);
        }, 0);

        return response;
      } catch (error) {
        console.error("expense getList failed", error);
        this.expenses = [];
        this.total = 0;
        this.fullTotal = 0;
      } finally {
        this.loading = false;
      }
    },

    editExpense(expense) {
      this.selected = expense;
      this.editDialog = true;
    },

    deleteExpense(expense) {
      if (
        this.currentBusiness.metadata &&
        this.currentBusiness.metadata.deletePrecaution
      ) {
        this.$swal
          .fire({
            title: "Are you sure?",
            html: `You won't be able to revert this.</br> <div style="color:red;">Type <span style="font-weight: bold">delete</span> in order remove this</div>`,
            icon: "question",
            input: "text",
            inputAttributes: {
              autocapitalize: "off",
            },
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Delete",
          })
          .then((result) => {
            if (result.value && result.value === "delete") {
              expenseService.delete(expense.id).then((response) => {
                if (response.data) {
                  this.$swal(
                    "Deleted!",
                    "Expense has been deleted.",
                    "success"
                  );
                  this.getList();
                }
              });
            } else if (result.dismiss === "cancel") {
            } else {
              this.$swal("Delete", 'Type "delete" to delete item', "error");
            }
          });
      } else {
        this.$swal({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "question",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          cancelButtonText: "No",
          confirmButtonText: "Yes",
        }).then((result) => {
          if (result.value) {
            expenseService.delete(expense.id).then((response) => {
              if (response.data) {
                this.$swal("Deleted!", "Expense has been deleted.", "success");
                this.getList();
              }
            });
          }
        });
      }
    },
  },
};
</script>

<style scoped>
.expense-list-page {
  background: #f8fafc;
}

.expense-list-card {
  border-radius: 20px;
  background: #ffffff;
}

.expense-list-header {
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.page-title {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
}

.page-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.expense-filter-bar {
  padding: 12px 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.expense-search {
  max-width: 340px;
  min-width: 260px;
}

.summary-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 220px));
  gap: 12px;
}

.summary-box {
  background: #f8fafc;
  border-radius: 16px;
  padding: 14px;
}

.summary-label {
  font-size: 12px;
  color: #64748b;
}

.summary-value {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  margin-top: 4px;
}

.expense-table {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
}

.expense-row:hover {
  background: #f8fafc;
}

.table-total-row {
  background: #f8fafc;
}

.table-result-row {
  background: #eef2ff;
}

@media (max-width: 600px) {
  .expense-search {
    max-width: 100%;
    min-width: 100%;
  }

  .summary-row {
    grid-template-columns: 1fr;
  }
}
</style>